import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import { labelWithOptionalRequiredIcon } from "ds_legacy/components/RequiredIcon";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { padding } from "ds_legacy/materials/metrics";
import { Body, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { MenuItem } from "dsl/atoms/MenuItem";
import { UndoIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAutocomplete, useInputCursorPos } from "./hooks";
import { AutocompleteModal } from "./modals";
import { FormValue, Requirement, Suggestion } from "./types";
import { languageTagMap } from "./utils";

function Autocomplete({
  complete,
  cursorPosition,
  description,
  name,
  requirements,
  setValue,
  value,
}: {
  complete: (arg: Array<string>) => string;
  cursorPosition: number;
  description: string;
  name: string;
  requirements: Requirement[];
  setValue: (v: string) => void;
  value: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <MenuItem
        sx={{ maxWidth: "100%", overflow: "hidden", padding: padding(1, 2) }}
        onClick={() => setOpen(true)}
      >
        <Body style={{ textOverflow: "ellipsis" }}>
          <b>{name}</b>
          <br />
          <i>{description}</i>
        </Body>
      </MenuItem>
      <AutocompleteModal
        isOpen={open}
        setOpen={setOpen}
        setVal={setValue}
        stringStart={cursorPosition}
        suggestion={{ complete, description, name, requirements }}
        val={value}
      />
    </>
  );
}

export const InputField = ({
  error,
  hasAutoFocus = false,
  helperText,
  id,
  isRequired = false,
  label,
  onReset,
  placeholder,
  rows = "3",
  setTags,
  setValue,
  suggestions,
  tabIndex,
  tags,
  value,
}: {
  error: string | null | undefined;
  hasAutoFocus?: boolean;
  helperText?: string;
  id?: string;
  isRequired?: boolean;
  label: string;
  onReset?: () => void;
  placeholder?: string;
  rows?: string;
  setTags?: (tags: Array<number>) => void;
  setValue: (v: FormValue) => void;
  suggestions?: Array<Suggestion>;
  tabIndex?: number;
  tags?: Array<number>;
  value: FormValue;
}) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const inputId = label.replace(" ", "");
  const cursorPos = useInputCursorPos(inputId);
  const autocomplete = useAutocomplete(value, cursorPos, suggestions);
  const isMultiline = useMemo(() => parseInt(rows) > 1, [rows]);
  const variant = useMemo(
    () => (isMultiline ? "outlined" : "standard"),
    [isMultiline],
  );
  const disabled = useMemo(() => {
    if (!id) return false;

    return tags?.some((tag) => [languageTagMap[id]?.ignore].includes(tag));
  }, [tags, id]);

  useEffect(() => {
    if (disabled && value !== "") setValue("");
  }, [disabled, value, setValue]);

  return (
    <div className="relative w-full">
      <FormControl error={error != null} fullWidth>
        <HorizontalLayout>
          <VerticalLayout stretched>
            <TextField
              autoFocus={hasAutoFocus}
              disabled={disabled}
              error={error != null}
              fullWidth
              helperText={!error && helperText}
              id={inputId}
              InputProps={{
                endAdornment: onReset && !disabled && (
                  <InputAdornment position="end">
                    <IconButton size="large" onClick={onReset}>
                      <UndoIcon size={FONT_SIZE_16} />
                    </IconButton>
                  </InputAdornment>
                ),
                inputProps: { tabIndex },
              }}
              label={labelWithOptionalRequiredIcon({
                isRequired,
                label,
              })}
              margin="normal"
              multiline={isMultiline}
              placeholder={placeholder}
              rows={rows}
              value={value || ""}
              variant={variant as ToType}
              onChange={(e) => setValue(e.target.value)}
            />
            <div ref={ref as ToType} className="relative -top-2.5" />
            <FormHelperText>{error != null && error}</FormHelperText>
          </VerticalLayout>
          {tags != null &&
            id != null &&
            languageTagMap[id] != null &&
            setTags != null && (
              <VerticalLayout style={{ padding: padding(0.5) }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tags.includes(languageTagMap[id].ignore)}
                      color="primary"
                      value={`${id}_ignore`}
                      onChange={() =>
                        tags.includes(languageTagMap[id].ignore)
                          ? setTags(
                              tags.filter(
                                (tag) => tag !== languageTagMap[id].ignore,
                              ),
                            )
                          : setTags(tags.concat(languageTagMap[id].ignore))
                      }
                    />
                  }
                  label="Ignore"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tags.includes(languageTagMap[id].todo)}
                      color="primary"
                      value={`${id}_todo`}
                      onChange={() =>
                        tags.includes(languageTagMap[id].todo)
                          ? setTags(
                              tags.filter(
                                (tag) => tag !== languageTagMap[id].todo,
                              ),
                            )
                          : setTags(tags.concat(languageTagMap[id].todo))
                      }
                    />
                  }
                  label="To do"
                />
              </VerticalLayout>
            )}
        </HorizontalLayout>
      </FormControl>
      {typeof value == "string" && (
        <Popper
          anchorEl={ref.current}
          open={autocomplete.length > 0}
          style={{ width: "40vw" }}
          transition
        >
          {({ placement, TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <div className="z-[100] flex flex-1 flex-col border-b border-gray-300 bg-white">
                  <MenuList>
                    {autocomplete.map((suggestion) => (
                      <Autocomplete
                        key={suggestion.name}
                        {...suggestion}
                        cursorPosition={cursorPos}
                        setValue={setValue}
                        value={value}
                      />
                    ))}
                  </MenuList>
                </div>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </div>
  );
};
